import { getTrimmedLabel } from '@helpers/getTrimmedLabel'
import { useLazyService } from '@hooks/useLazyService'
import { useLeadsContext } from '@pages/Leads/routes/Leads/contexts/index'
import { editLead } from '@services/leads/editLead'
import { Lead, LeadField } from '@services/leads/types'
import { Button, Input, ModalBody, Select, Spacer, Spreader } from '@ui-kit'
import { useFormik } from 'formik'
import { Fragment, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Row } from 'simple-flexbox'
import { v4 as uuidv4 } from 'uuid'

import {
  Checkbox,
  OFF,
  ON,
  removeLabelFromCheckboxValue,
  removeOnOffFromCheckboxValue
} from '../Table/Fields/Checkbox'
import styles from './DetailsModal.module.scss'
import { SourceField } from './Fields'

const createInitialValues = (
  fields: LeadField[]
): Record<string, LeadField> => {
  return fields.reduce((acc: Record<string, LeadField>, field: LeadField) => {
    return {
      ...acc,
      [uuidv4()]: { ...field }
    }
  }, {})
}

export const EditFields = ({
  lead,
  exitEditMode
}: {
  lead: Lead
  exitEditMode: () => void
}) => {
  const { t } = useTranslation()
  const modalBodyRef = useRef<HTMLDivElement>(null)

  const { attributes, id } = lead
  const { source_type, source_name, fields, source_uuid, source_href } =
    attributes

  const { refreshCurrentLeadsList } = useLeadsContext()

  const [handleEditLead, { isLoading }] = useLazyService(editLead, {
    forwardError: true,
    handleErrorMessages: true,
    t,
    successToastText: t('toast.saved.success')
  })

  const handleOptimisticRefresh = async (fields: LeadField[]) => {
    refreshCurrentLeadsList(
      async currentData => {
        if (!currentData) return currentData

        const optimisticData = {
          ...currentData,
          collection: currentData.collection.map(currentLead => {
            if (currentLead.id === id) {
              const editedLead = {
                ...currentLead,
                attributes: {
                  ...currentLead.attributes,
                  fields
                }
              }

              return editedLead
            }

            return currentLead
          })
        }

        return optimisticData
      },
      { revalidate: false, rollbackOnError: true }
    )
  }

  const { values, setValues, handleSubmit } = useFormik({
    initialValues: createInitialValues(fields),
    onSubmit: async () => {
      const fields = Object.entries(values).map(([, field]) => {
        if (field.subtype === 'custom') {
          const { subtype, ...rest } = field
          return rest
        }

        return field
      })

      try {
        await handleEditLead({
          uuid: id,
          fields,
          sourceUuid: source_uuid,
          sourceType: source_type
        })

        handleOptimisticRefresh(fields)

        exitEditMode()
      } catch {
        // do nothing
      }
    }
  })

  const handleAddField = () => {
    const uuid = uuidv4()

    const newField = {
      name: `custom_${uuid}`,
      subtype: 'custom',
      label: '',
      value: ''
    }

    setValues({
      ...values,
      [uuidv4()]: newField
    })

    setTimeout(() => {
      modalBodyRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'end'
      })
    }, 10)
  }

  const handleFieldChange = (
    id: string,
    key: 'value' | 'label',
    newValue: string
  ) => {
    setValues({
      ...values,
      [id]: { ...values[id], [key]: newValue }
    })
  }

  const handleDeleteField = (id: string) => {
    const newValues = { ...values }
    delete newValues[id]

    setValues(newValues)
  }

  const renderCustomFields = ([id, field]: [string, LeadField]) => {
    return (
      <Fragment>
        <Spacer space={15} />

        <Row>
          <Input
            className={styles.input}
            name={`${id}-label`}
            size={16}
            value={field.label}
            i18n={{ label: t('word.field.name') }}
            id={`${id}-label`}
            onChange={e => handleFieldChange(id, 'label', e.target.value)}
            required={false}
          />

          <Spreader spread={10} />

          <Input
            className={styles.input}
            name={`${id}-value`}
            size={16}
            value={field.value}
            i18n={{ label: t('word.field.value') }}
            id={`${id}-value`}
            onChange={e => handleFieldChange(id, 'value', e.target.value)}
            required={false}
          />

          <Button
            icon='icon-trash'
            size={14}
            variant='text-neutral'
            onClick={() => handleDeleteField(id)}
          />
        </Row>

        <Spacer space={15} />
      </Fragment>
    )
  }

  const renderInputField = ([id, field]: [string, LeadField]) => {
    if (field.subtype === 'custom') {
      return renderCustomFields([id, field])
    }

    if (field.subtype === 'checkbox') {
      const checkboxValue = removeLabelFromCheckboxValue(field.value)
      const checkboxLabel = removeOnOffFromCheckboxValue(field.value)

      const { trimmedLabel } = getTrimmedLabel({
        label: field.label,
        maxNumberOfCharacters: 35
      })

      return (
        <Fragment key={id}>
          <Spacer space={15} />

          <Row justifyContent='space-between'>
            <Select
              options={[
                {
                  value: OFF,
                  label: <Checkbox value={OFF} label='' size={10} />
                },
                {
                  value: ON,
                  label: <Checkbox value={ON} label='' size={10} />
                }
              ]}
              value={checkboxValue}
              onChange={value =>
                handleFieldChange(id, 'value', `(${value}) ${checkboxLabel}`)
              }
              size='small'
              overflowStyle={{ maxHeight: '220px' }}
              layerClassName={styles['checkbox-select']}
            />

            <Spreader spread={10} />

            <Input
              className={styles['checkbox-input']}
              name={id}
              size={16}
              value={checkboxLabel}
              i18n={{ label: trimmedLabel }}
              id={id}
              onChange={e => {
                handleFieldChange(
                  id,
                  'value',
                  `(${checkboxValue}) ${e.target.value}`
                )
              }}
              required={false}
            />

            <Button
              icon='icon-trash'
              size={14}
              variant='text-neutral'
              onClick={() => handleDeleteField(id)}
            />
          </Row>

          <Spacer space={15} />
        </Fragment>
      )
    }

    const { trimmedLabel } = getTrimmedLabel({
      label: field.label,
      maxNumberOfCharacters: 35
    })

    return (
      <Fragment key={id}>
        <Spacer space={15} />

        <Row>
          <Input
            className={styles.input}
            name={id}
            size={16}
            value={field.value}
            i18n={{ label: trimmedLabel }}
            id={id}
            onChange={e => handleFieldChange(id, 'value', e.target.value)}
            required={false}
          />

          <Button
            icon='icon-trash'
            size={14}
            variant='text-neutral'
            onClick={() => handleDeleteField(id)}
          />
        </Row>

        <Spacer space={15} />
      </Fragment>
    )
  }

  return (
    <form onSubmit={handleSubmit} className={styles.detailsWrapper}>
      <ModalBody className={styles.fields}>
        <div ref={modalBodyRef}>
          <SourceField
            source_name={source_name}
            source_type={source_type}
            source_href={source_href}
          />
          {Object.entries(values).map(renderInputField)}
        </div>
      </ModalBody>
      <ModalBody className={styles.footer}>
        <Row justifyContent='space-between'>
          <Button
            variant='text-neutral'
            icon='icon-plus'
            onClick={handleAddField}
          >
            {t('word.add.field')}
          </Button>

          <Row>
            <Button variant='text-primary' onClick={exitEditMode}>
              {t('word.cancel')}
            </Button>
            <Spreader spread={20} />
            <Button
              variant='primary'
              type='submit'
              disabled={isLoading}
              isLoading={isLoading}
            >
              {t('word.save')}
            </Button>
          </Row>
        </Row>
      </ModalBody>
    </form>
  )
}
